import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import ViewEditDialog from "../../../../utils/ui-components/ViewEditDialog";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MainCard from "../../../../utils/ui-components/MainCard";
import { gridSpacing } from "../../../../store/constants";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmptyResult from "../../../../utils/ui-components/EmptyResult";
import Spinner from "../../../../utils/ui-components/Spinner";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import AddUser from "./createParentUser/user-operator-configuration";
import { formatMobile } from "../../../../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { UserService } from "../../../../assets/_services/user-service";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextFieldWrapper from "../../../../utils/ui-components/FormsUI/TextField";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useDispatch } from "react-redux";
import { MENU_OPEN } from "../../../../redux/actions/actions";
import { DataGrid, GridAlignment } from "@mui/x-data-grid";
import EmptyResultDataGrid from "../../../../utils/ui-components/EmptyResultDataGrid";

const INDUSTRY_TYPES = [
  {
    value: 1,
    label: "Construction",
  },
  {
    value: 2,
    label: "Retail",
  },
  {
    value: 3,
    label: "Transportation",
  },
  {
    value: 4,
    label: "Financial Service",
  },
  {
    value: 5,
    label: "Agriculture",
  },
  {
    value: 6,
    label: "Education",
  },
  {
    value: 7,
    label: "Technology",
  },
  {
    value: 8,
    label: "Health Care",
  },
];

function UserList({ role }: any) {
  const theme: any = useTheme();
  let { state } = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState(state?.search || "");
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(state?.count || 0);

  const path = useLocation();
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState(
    state?.paginationModel || {
      page: 0,
      pageSize: 10,
    }
  );

  useEffect(() => {
    const id = path?.pathname;
    dispatch({ type: MENU_OPEN, id });
    window.history.replaceState({}, "");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchApiUserList(search);
  }, [paginationModel]);

  const fetchApiUserList = (searchValue: string) => {
    setIsLoading(true);
    let formData = {
      userName: searchValue,
    };
    UserService.getAllOwnerList(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
      if (response.isSuccess) {
        setUsers(response.data.data.rows);
        setCount(response.data.data.count);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const columns = [
    {
      field: "action",
      filterable: false,
      headerName: "",
      sortable: false,
      disableExport: false,
      minWidth: 75,
      maxWidth: 75,
      headerClassName: "stickyActionHeader",
      cellClassName: "stickyColumn",
      renderCell: (params: any) => {
        return (
          <Stack direction={"row"}>
            <Tooltip title="Create Child">
              <IconButton
                aria-label="view"
                color="primary"
                onClick={() => {
                  navigate(`/child/list/${params.row?.id}`, {
                    state: { paginationModel: paginationModel, count: count, search: search, return: "/config/users" },
                  });
                }}
              >
                <PersonAddIcon fontSize="inherit" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View">
              <IconButton
                aria-label="view"
                color="primary"
                onClick={() => {
                  navigate(`/config/users/${params.row?.id}`, {
                    state: { paginationModel: paginationModel, count: count, search: search },
                  });
                }}
              >
                <VisibilityIcon fontSize="inherit" color="primary" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "id",
      filterable: false,
      headerName: "ID",
      sortable: false,
      disableExport: false,
      minWidth: 60,
      maxWidth: 80,
    },
    {
      field: "dp",
      filterable: false,
      headerName: "User",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      renderCell: (params: any) => {
        return (
          <Stack alignItems={"center"} direction={"row"} spacing={1}>
            <Avatar src={`${process.env.REACT_APP_API_PROD}/${params.row?.companyImage}`} />
            <Stack direction={"column"}>
              <Typography>{params?.row?.companyName || ""}</Typography>
              <Typography variant="caption">{params?.row?.representativeName || ""}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: "userName",
      filterable: false,
      headerName: "Username",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "email",
      filterable: false,
      headerName: "Email",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "accountType",
      filterable: false,
      headerName: "Account Type",
      sortable: false,
      disableExport: false,
      minWidth: 120,
      align: "center" as GridAlignment,
      flex: 1,
      renderCell: (params: any) => {
        return params.row?.accountType === 1 ? (
          <Typography fontWeight={"bold"} variant="overline" color="primary">
            Postpaid
          </Typography>
        ) : (
          <Typography fontWeight={"bold"} variant="overline" color="secondary">
            Prepaid
          </Typography>
        );
      },
    },
    {
      field: "industryType",
      filterable: false,
      headerName: "Industry Type",
      sortable: false,
      disableExport: false,
      minWidth: 130,
      align: "center" as GridAlignment,
      flex: 1,
      renderCell: (params: any) => {
        return INDUSTRY_TYPES.find((industry: any) => industry?.value === params.row?.industryType)?.label;
      },
    },
    {
      field: "contact",
      filterable: false,
      headerName: "Contact",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      maxWidth: 150,
      flex: 1,
      renderCell: (params: any) => {
        return formatMobile(params.row?.mobile || "");
      },
    },
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      sortable: false,
      disableExport: false,
      minWidth: 100,
      maxWidth: 130,
      align: "center" as GridAlignment,
      flex: 1,
      renderCell: (params: any) => {
        return params.row?.status ? (
          <Chip variant="outlined" color="success" sx={{ borderRadius: 2 }} label="Active" />
        ) : (
          <Chip variant="outlined" color="error" label="Inactive" sx={{ borderRadius: 2 }} />
        );
      },
    },
  ];

  return (
    <Grid container>
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <Formik
            initialValues={{
              userName: state?.search || "",
            }}
            validationSchema={Yup.object().shape({
              userName: Yup.string().notRequired(),
            })}
            onSubmit={(values: any, { setSubmitting }) => {
              setPaginationModel({ ...paginationModel, page: 0 });
              setSearch(values.userName || "");
              fetchApiUserList(values.userName);
            }}
          >
            {({ values, dirty, isSubmitting, isValid, setFieldValue }) => (
              <Form>
                <Grid container justifyContent={"flex-end"} spacing={1}>
                  <Grid item>
                    <TextFieldWrapper
                      label="Search"
                      placeholder="Username.."
                      name="userName"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item>
                    <Button color="primary" variant="contained" sx={{ ...theme.typography.customInput }} type="submit">
                      View
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      color="primary"
                      variant="contained"
                      sx={{ ...theme.typography.customInput }}
                      type="submit"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        navigate("/config/users/create", { state: null });
                      }}
                    >
                      Create User
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ minHeight: 400 }}>
            <DataGrid
              autoHeight
              columns={columns}
              loading={isLoading}
              rows={users}
              slots={{
                noRowsOverlay: EmptyResultDataGrid,
                loadingOverlay: () => <LinearProgress />,
              }}
              style={{ overflow: "auto" }}
              sx={{
                "& .MuiDataGrid-cell.stickyColumn": {
                  position: "sticky",
                  left: 0,
                  backgroundColor: `${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.background.paper}`,
                  zIndex: 1,
                },
                "& .clicked-row": {
                  backgroundColor: `${theme.palette.secondary.light} !important`,
                },
                // cursor: "pointer",
              }}
              pagination
              density="standard"
              getRowId={(row: any) => row.id}
              paginationMode="server"
              rowCount={count}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default UserList;
